import React, { useEffect, useState } from "react";
import { salaryBenchmark, getScoreTier } from "@/lib/api";
import { TrendingUp, Zap, Building2, Award } from "lucide-react";
import PageLoader from "@/components/wishire/PageLoader";

export default function SalaryPage() {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    salaryBenchmark().then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    getScoreTier().then(r => setTier(r.data)).catch(() => {});
  }, []);

  if (loading) return <PageLoader text="Fetching salary data for your profile…" />;
  if (!data) return <div className="container-x py-14 text-stone-500">Could not load salary data. Set a target role on your dashboard first.</div>;

  const demandColor = { "Very high": "text-emerald-600 bg-emerald-50", "High": "text-blue-600 bg-blue-50", "Moderate": "text-amber-600 bg-amber-50", "Low": "text-red-600 bg-red-50" }[data.market_demand] || "text-stone-600 bg-stone-100";

  const worth = scoreWorth(tier, data);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Salary benchmark</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          What you should{" "}
          <span className="italic text-stone-500">be earning.</span>
        </h1>
        <p className="mt-3 text-stone-500">Based on your role, city, experience and skills — Indian market 2025.</p>
      </div>

      {/* Main salary card */}
      <div className="rounded-3xl bg-stone-900 text-stone-50 p-10 md:p-12 wsh-grain mb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{background:"radial-gradient(600px 400px at 80% 20%, rgba(217,119,6,0.5), transparent 60%)"}}/>
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <p className="overline mb-2" style={{color:"rgba(253,251,247,0.55)"}}>
              {data.role} · {data.city} · {data.experience_band}
            </p>
            <div className="flex items-end gap-4 mb-4">
              <div>
                <p className="text-stone-400 text-sm mb-1">Min</p>
                <p className="font-display text-4xl">₹{data.salary_min_lpa}<span className="text-xl text-stone-400"> LPA</span></p>
              </div>
              <div className="text-stone-500 text-2xl mb-1">—</div>
              <div>
                <p className="text-stone-400 text-sm mb-1">Max</p>
                <p className="font-display text-4xl">₹{data.salary_max_lpa}<span className="text-xl text-stone-400"> LPA</span></p>
              </div>
            </div>
            <p className="text-stone-300">Median: <span className="font-display text-2xl text-amber-300">₹{data.median_lpa} LPA</span></p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl bg-white/10 p-4">
              <p className="text-xs text-stone-400 uppercase tracking-widest font-mono mb-1">Market demand</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${demandColor}`}>{data.market_demand}</span>
            </div>
            {data.remote_premium_percent > 0 && (
              <div className="rounded-2xl bg-white/10 p-4">
                <p className="text-xs text-stone-400 uppercase tracking-widest font-mono mb-1">Remote premium</p>
                <p className="font-display text-2xl text-amber-300">+{data.remote_premium_percent}%</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* What your Wishire Score is worth */}
      {worth && (
        <div data-testid="salary-score-worth" className="rounded-3xl border border-[#C2410C]/25 bg-gradient-to-r from-[#fff7ed] to-[#fefce8] p-6 md:p-7 mb-6 flex flex-col md:flex-row md:items-center gap-5">
          <span className="inline-flex p-3 rounded-2xl bg-[#C2410C] text-white shrink-0">
            <Award size={22} />
          </span>
          <div className="flex-1">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">What your score is worth</p>
            <p className="font-display text-xl text-stone-900 mt-0.5">
              Your {worth.tierName} score ({worth.score}) puts you in the {worth.band} of this band.
            </p>
            <p className="text-sm text-stone-600 mt-1">{worth.advice}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Anchor your ask near</p>
            <p className="font-display text-3xl text-[#15803D]">₹{worth.target} <span className="text-lg text-stone-400">LPA</span></p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top paying companies */}
        <div className="rounded-3xl border border-stone-200 bg-white p-8">
          <div className="flex items-center gap-2 mb-5">
            <Building2 size={18} className="text-stone-500"/>
            <h2 className="font-display text-xl">Top paying companies</h2>
          </div>
          <ol className="space-y-3">
            {(data.top_paying_companies || []).map((c, i) => (
              <li key={c} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-stone-100 text-stone-500 text-xs font-display flex items-center justify-center">{i+1}</span>
                <span className="text-stone-800 text-sm font-medium">{c}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Salary boosters */}
        <div className="rounded-3xl border border-stone-200 bg-white p-8">
          <div className="flex items-center gap-2 mb-5">
            <Zap size={18} className="text-amber-500"/>
            <h2 className="font-display text-xl">Salary boosters</h2>
          </div>
          <p className="text-sm text-stone-500 mb-4">Skills that push your pay above median</p>
          <div className="flex flex-wrap gap-2">
            {(data.salary_boosters || []).map(b => (
              <span key={b} className="px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">{b}</span>
            ))}
          </div>
        </div>

        {/* Negotiation tip */}
        <div className="rounded-3xl border border-stone-200 bg-white p-8">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp size={18} className="text-[#C2410C]"/>
            <h2 className="font-display text-xl">Negotiation tip</h2>
          </div>
          <p className="text-stone-700 leading-relaxed text-sm">{data.negotiation_tip}</p>
        </div>
      </div>
    </div>
  );
}

// Map the candidate's Wishire Score tier to where in the salary band to anchor.
function scoreWorth(tier, data) {
  if (!tier || !data) return null;
  const score = Math.round(tier.adjusted_score ?? tier.raw_score ?? 0);
  if (!score) return null;
  const tierName = tier.tier?.name || "current";
  const min = Number(data.salary_min_lpa) || 0;
  const max = Number(data.salary_max_lpa) || 0;
  const median = Number(data.median_lpa) || Math.round((min + max) / 2);
  if (!max) return null;

  let band, advice, target;
  if (score >= 90) {
    band = "very top"; target = max;
    advice = "Top-decile candidate — anchor at the ceiling and negotiate a premium.";
  } else if (score >= 80) {
    band = "upper part"; target = Math.round((median + max) / 2);
    advice = "Strong profile — target above median, push toward the max.";
  } else if (score >= 70) {
    band = "middle-to-upper part"; target = median;
    advice = "Competitive — anchor at median and let your strengths pull it up.";
  } else if (score >= 60) {
    band = "middle"; target = Math.round((min + median) / 2);
    advice = "Solid baseline — raise your score to unlock the upper band.";
  } else {
    band = "lower part"; target = min;
    advice = "Build your Wishire Score (résumé + interview) to command more.";
  }
  return { score, tierName, band, advice, target };
}
